'use client'

import React, { ChangeEvent, HTMLAttributes, ReactNode, Ref, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import { ChangeHandler } from 'react-hook-form'
import { useInputWrapper } from '../../../hooks/useInputWrapper'
import { Button, ButtonVariants } from '../../atoms'
import { FormNumberInputStepControls } from './FormNumberInputStepControls'

const numberInputFieldClassName = (controlsAreOnLeft: boolean, controlsAreSplit: boolean): string => {
    let className = 'w-full px-3 py-2 density-lg:py-3 flex-1 outline-0 bg-transparent placeholder:italic'
    if (controlsAreOnLeft) className += ' text-right'
    if (controlsAreSplit) className += ' text-center'
    return className
}

export enum ControlsPosition {
    LEFT = 'left',
    SPLIT = 'split',
    RIGHT = 'right',
}

export interface FormNumberInputProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
    disabled?: boolean
    form?: string
    readOnly?: boolean
    error?: boolean
    id?: string
    name?: string
    prefix?: string
    suffix?: string | ReactNode
    showControls?: boolean
    controlsPosition?: ControlsPosition
    min?: number
    max?: number
    step?: number
    onChange?: (value: number | string) => void
    onBlur?: ChangeHandler
    onClickSuffix?: () => void
    value?: number | string
    autoFocus?: boolean
    required?: boolean
    inputRef?: Ref<HTMLInputElement>
}

export const FormNumberInput = ({
    disabled,
    error,
    prefix,
    form,
    suffix,
    showControls,
    controlsPosition = ControlsPosition.RIGHT,
    min = 0,
    max,
    step = 1,
    onChange,
    onBlur,
    onClickSuffix,
    readOnly = false,
    value,
    autoFocus = false,
    inputRef,
    ...rest
}: FormNumberInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled, error)
    const localInputRef = useRef<HTMLInputElement | null>(null)

    /**
     * Callback ref for the {@link HTMLInputElement}: keeps an internal reference for
     * programmatic work (e.g. dispatching a native `change` after +/-) and forwards the node to
     * {@link FormNumberInputProps.inputRef} when provided (callback ref or object ref).
     *
     * @param element - The mounted input, or `null` when unmounting.
     */
    const setInputRef = useCallback(
        (element: HTMLInputElement | null) => {
            localInputRef.current = element
            if (typeof inputRef === 'function') {
                inputRef(element)
            } else if (inputRef != null) {
                ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = element
            }
        },
        [inputRef],
    )

    const dispatchNativeChangeEvent = useCallback(() => {
        const input = localInputRef.current
        if (!input || disabled || readOnly || !showControls) return
        if (!input.form) return
        input.dispatchEvent(new Event('change', { bubbles: true }))
    }, [disabled, readOnly, showControls])

    /** Updates parent state before dispatching a native `change` so `target.value` matches. */
    const applyValueFromStepControls = useCallback(
        (newValue: number) => {
            if (disabled || readOnly || !onChange) return
            flushSync(() => {
                onChange(newValue)
            })
            dispatchNativeChangeEvent()
        },
        [disabled, readOnly, onChange, dispatchNativeChangeEvent],
    )

    const controlsAreOnLeft = controlsPosition === ControlsPosition.LEFT
    const controlsAreSplit = controlsPosition === ControlsPosition.SPLIT

    const increment = () => {
        if (disabled || readOnly) return
        let newValue: number
        if (typeof value !== 'number') {
            newValue = step
        } else {
            const stepped = (value ?? min) + step
            newValue = typeof max === 'number' ? Math.min(stepped, max) : stepped
        }
        applyValueFromStepControls(newValue)
    }

    const decrement = () => {
        if (disabled || readOnly) return
        const newValue: number = typeof value !== 'number' ? min : Math.max((value ?? min) - step, min)
        applyValueFromStepControls(newValue)
    }

    const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const inputValue = event.target.value
        const hasEmptyInput = inputValue === ''
        const nextValue: string | number = hasEmptyInput ? inputValue : parseFloat(inputValue)
        onChange?.(nextValue)
    }

    return (
        <div className={`flex items-center border w-full ${classes} ${rest.className || ''}`} data-cy="input-number">
            {prefix && <span className="pl-3 text-shade-600">{prefix}</span>}

            <input
                {...rest}
                disabled={disabled}
                readOnly={readOnly}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                form={form}
                className={numberInputFieldClassName(controlsAreOnLeft, controlsAreSplit)}
                onFocus={onFocusHandler}
                onBlur={(event) => {
                    onBlur && onBlur(event)
                    onBlurHandler()
                }}
                onChange={changeHandler}
                value={value}
                min={min}
                max={max}
                step={step}
                type="number"
                autoFocus={autoFocus}
                formNoValidate
                aria-invalid={error}
                ref={setInputRef}
            />

            {suffix && !onClickSuffix && <span className="pr-3 text-shade-600">{suffix}</span>}

            {suffix && onClickSuffix && (
                <Button variant={ButtonVariants.QUIET} onClick={onClickSuffix}>
                    {suffix}
                </Button>
            )}

            {showControls && (
                <FormNumberInputStepControls
                    controlsAreOnLeft={controlsAreOnLeft}
                    controlsAreSplit={controlsAreSplit}
                    onDecrement={decrement}
                    onIncrement={increment}
                />
            )}
        </div>
    )
}
